<template>
  <v-container>
    <v-row>
      <v-col :cols="12" md="10" offset-md="1" lg="8" offset-lg="2">
        <v-card>
          <v-card-title>Scots Connect Family Edition: Password Reset</v-card-title>
          <template v-if="complete">
            <v-card-text>
              <p>Your password has been reset. Click the button below to return to the login screen so you can log in, making sure to select the Parent Login in the dropdown.</p>
            </v-card-text>
            <v-card-actions>
              <v-btn color="success" to="/">Proceed to login screen</v-btn>
            </v-card-actions>
          </template>
          <template v-else>
            <v-card-text>To reset your password, please enter the email address you used when creating the account. We will send you an email with a unique code to enter to verify your email. Once that is done you will be able to set a new password.</v-card-text>
            <v-stepper v-model="step">
              <v-stepper-header>
                <v-stepper-step :complete="step > 1" step="1">Email</v-stepper-step>
                <v-divider></v-divider>
                <v-stepper-step :complete="step > 2" step="2">Verify</v-stepper-step>
                <v-divider></v-divider>
                <v-stepper-step step="3">New Password</v-stepper-step>
              </v-stepper-header>
              <v-stepper-items>
                <v-stepper-content step="1">
                  <v-card style="min-height: 300px">
                    <v-card-text>
                      <p>Please enter the email address that you used to create your Scots Connect account.</p>
                      <v-form ref="emailForm">
                        <v-text-field v-model="email" :rules="emailRules" label="Login Email" type="email" outlined @keypress.enter.prevent="checkEmail"></v-text-field>
                      </v-form>
                      <v-alert v-if="sendingCode" type="info">Sending a unique code to your email for validation. This should only take a few seconds.</v-alert>
                    </v-card-text>
                    <v-card-actions>
                      <v-btn color="primary" :disabled="sendingCode" @click="checkEmail">Next</v-btn>
                      <v-spacer></v-spacer>
                      <v-btn outlined to="/">Cancel</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-stepper-content>
                <v-stepper-content step="2">
                  <v-card style="min-height: 300px">
                    <v-card-text>
                      <p>An email was just sent to you from studentdevelopment@covenant.edu with a subject of "Scots Connect Code" which contains a one-time-use code. Please log into your email to retrieve that code and copy-and-paste it below. Note that the code expires one hour from when the email was sent.</p>
                      <v-text-field v-model="oneTimeCode" label="Code from Email" outlined @keypress.enter.prevent="verifyCode"></v-text-field>
                      <v-alert v-if="validationMessage !== ''" type="error">{{ validationMessage }}</v-alert>
                    </v-card-text>
                    <v-row>
                      <v-col></v-col>
                    </v-row>
                    <v-card-actions>
                      <v-btn @click="step--">Back</v-btn>
                      <v-btn color="success" @click="verifyCode">Next</v-btn>
                      <v-spacer></v-spacer>
                      <v-btn outlined to="/">Cancel</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-stepper-content>
                <v-stepper-content step="3">
                  <v-card style="min-height: 300px">
                    <v-card-text>
                      <p>Your password must be at least 8 characters long and must contain at least one lower-case letter, one capital letter, and one number.</p>
                      <v-form ref="passwordForm">
                        <v-row>
                          <v-col :cols="12" md="6" style="padding-bottom:0;padding-top:0">
                            <v-text-field v-model="password" :rules="passwordRule" label="Password" type="password" outlined @keypress.enter.prevent="password2.focus()"></v-text-field>
                          </v-col>
                          <v-col :cols="12" md="6" style="padding-bottom:0;padding-top:0">
                            <v-text-field v-model="password2" :rules="secondPasswordCheck" label="Repeat Password" type="password" outlined @keypress.enter.prevent="resetPassword"></v-text-field>
                          </v-col>
                        </v-row>
                      </v-form>
                    </v-card-text>
                    <v-card-actions>
                      <v-btn @click="step--">Back</v-btn>
                      <v-btn color="success" @click="resetPassword">Set Password</v-btn>
                      <v-spacer></v-spacer>
                      <v-btn outlined to="/">Cancel</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-stepper-content>
              </v-stepper-items>
            </v-stepper>
          </template>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import { ref } from '@vue/composition-api'
export default {
  setup (props, { root }) {
    const step = ref(1)
    const emailForm = ref(null)
    const email = ref('')
    const emailRules = ref([v => !!v || 'Required', v => /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(v) || 'Invalid email'])
    const sendingCode = ref(false)
    async function checkEmail () {
      if (emailForm.value.validate()) {
        sendingCode.value = true
        // Submit the data to the server which will generate a unique code
        try {
          await root.$feathers.service('system/unique-code').create({ email: email.value, source: 'orientation' })
          step.value++
        } catch (e) {
          root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 8000, text: 'Error sending code: ' + e })
        }
      }
    }

    const oneTimeCode = ref('')
    const validationMessage = ref('')
    async function verifyCode () {
      validationMessage.value = ''
      try {
        const res = await root.$feathers.service('system/unique-code').find({ query: { email: email.value, code: oneTimeCode.value } })
        if (res.valid) {
          const { data } = await root.$feathers.service('orientation/parent').find({ query: { email: email.value } })
          if (data.length === 0) {
            validationMessage.value = 'The email address that you entered is not set up in Scots Connect. Please create a new account to proceed.'
          } else {
            step.value++
          }
        } else {
          validationMessage.value = 'The code that you entered was not valid. Please ensure that you copy the whole code and that it has not expired. If you need to generate a new code, please click the "Back" button below then press "Next" again to generate a new code.'
        }
      } catch (e) {
        root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 8000, text: 'Error processing validation: ' + e })
        validationMessage.value = e
      }
    }

    const passwordForm = ref(null)
    const password = ref('')
    const password2 = ref('')
    const passwordRule = ref([
      v => v.length >= 8 || 'Must be at least 8 characters long',
      v => /[a-z]/.test(v) || 'Must contain a lower-case letter',
      v => /[A-Z]/.test(v) || 'Must contain an upper-case letter',
      v => /[\d]/.test(v) || 'Must contain a number'
    ])
    const secondPasswordCheck = ref([v => v === password.value || 'Passwords do not match'])
    const complete = ref(false)
    async function resetPassword () {
      if (passwordForm.value.validate()) {
        let obj = {
          email: email.value,
          password: password.value,
          code: oneTimeCode.value
        }
        try {
          await root.$feathers.service('orientation/parent').patch(email, obj)
        } catch (e) {
          root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 8000, text: 'Error resetting password: ' + e })
        }
        complete.value = true
      }
    }

    return {
      step,
      emailForm,
      email,
      emailRules,
      sendingCode,
      checkEmail,
      oneTimeCode,
      validationMessage,
      verifyCode,
      passwordForm,
      password,
      password2,
      passwordRule,
      secondPasswordCheck,
      complete,
      resetPassword
    }
  }
}
</script>
